import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { Button, Card, FormField, Input } from '../../components/ui';
import { api, type TotpEnrollmentStart } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { useAuth } from '../../lib/auth';
import { knownTenantSlugForHost } from '../../lib/knownTenantDomains';

// Login failures must stay DELIBERATELY VAGUE about whether the email exists or which part was
// wrong (apps/api/src/routes/dashboard/auth.ts's invalidCreds(): "the same error either way -
// unknown tenant vs unknown user vs wrong password"). toParsedError/ErrorSummary is still safe to
// use here: the server's `code` for this failure is `unauthenticated`, which parseApiError
// classifies as the neutral `other` kind (not `validation`, the only kind that rewrites
// `message`) - so the exact same vague server string is shown unchanged, just now with a request
// ID attached for support, which reveals nothing about account existence.

type Step = 'credentials' | 'totp' | 'enroll' | 'recovery-codes';

export function LoginPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState<Step>('credentials');
  const knownTenantSlug = knownTenantSlugForHost();
  const [tenantSlug, setTenantSlug] = useState(knownTenantSlug ?? '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [enrollmentPendingToken, setEnrollmentPendingToken] = useState<string | null>(null);
  const [enrollment, setEnrollment] = useState<TotpEnrollmentStart | null>(null);
  const [enrollCode, setEnrollCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [error, setError] = useState<ParsedApiError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleCredentials(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.auth.login(email, password, tenantSlug);
      if (result.totpRequired) {
        setPendingToken(result.pendingToken ?? null);
        setStep('totp');
      } else if (result.enrollmentRequired && result.enrollmentPendingToken) {
        // This tenant forces staff 2FA (tenant_settings.staff2faForced, docs/WALKTHROUGH.md "2FA"
        // point 5) - the password check already passed, so start enrollment immediately.
        setEnrollmentPendingToken(result.enrollmentPendingToken);
        const started = await api.auth.enrollStart(result.enrollmentPendingToken);
        setEnrollment(started);
        setStep('enroll');
      } else {
        await refresh();
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(toParsedError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleTotp(event: FormEvent) {
    event.preventDefault();
    if (!pendingToken) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.auth.loginTotp(pendingToken, totpCode);
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(toParsedError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleEnrollConfirm(event: FormEvent) {
    event.preventDefault();
    if (!enrollmentPendingToken) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.auth.enrollConfirm(enrollmentPendingToken, enrollCode);
      setRecoveryCodes(result.recoveryCodes);
      setStep('recovery-codes');
    } catch (err) {
      setError(toParsedError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRecoveryCodesContinue() {
    await refresh();
    navigate('/', { replace: true });
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg px-4">
      <Card className="w-full max-w-sm" title="Staff Login">
        {step === 'recovery-codes' ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-slate-600">
              Save these recovery codes somewhere safe. Each one can be used once, instead of a
              code from your authenticator app, if you ever lose access to it. For your security,
              you will not be able to see them again after this.
            </p>
            <ul className="grid grid-cols-2 gap-2 rounded bg-slate-100 p-3 font-mono text-sm">
              {recoveryCodes.map((code) => (
                <li key={code}>{code}</li>
              ))}
            </ul>
            <Button className="w-full justify-center" onClick={handleRecoveryCodesContinue}>
              I've saved these codes - continue
            </Button>
          </div>
        ) : step === 'enroll' ? (
          <form className="flex flex-col gap-1" onSubmit={handleEnrollConfirm}>
            <p className="mb-3 text-sm text-slate-600">
              This organization requires two-factor authentication. Scan this QR code with an
              authenticator app (Google Authenticator, 1Password, Authy, ...), then enter the
              6-digit code it shows.
            </p>
            {enrollment && (
              <div
                className="mx-auto mb-3 h-48 w-48 [&_svg]:h-full [&_svg]:w-full"
                dangerouslySetInnerHTML={{ __html: enrollment.qrSvg }}
              />
            )}
            {enrollment && (
              <p className="mb-3 text-center text-xs text-slate-500">
                Can't scan it? Enter this code manually:{' '}
                <code className="font-mono">{enrollment.manualEntryKey}</code>
              </p>
            )}
            <FormField label="Verification code" htmlFor="enroll-code">
              <Input
                id="enroll-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={enrollCode}
                onChange={(e) => setEnrollCode(e.target.value)}
              />
            </FormField>
            {error && <ErrorSummary error={error} className="mb-1" />}
            <Button
              type="submit"
              disabled={submitting}
              className="w-full justify-center mt-2"
            >
              {submitting ? 'Confirming…' : 'Confirm and finish setup'}
            </Button>
          </form>
        ) : step === 'totp' ? (
          <form className="flex flex-col gap-1" onSubmit={handleTotp}>
            <p className="mb-3 text-sm text-slate-600">
              Enter the 6-digit verification code from your authenticator app.
            </p>
            <FormField label="Verification code" htmlFor="totp-code">
              <Input
                id="totp-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                autoFocus
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
              />
            </FormField>
            {error && <ErrorSummary error={error} className="mb-1" />}
            <Button type="submit" disabled={submitting} className="w-full justify-center mt-2">
              {submitting ? 'Verifying…' : 'Verify'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full justify-center"
              onClick={() => {
                setPendingToken(null);
                setTotpCode('');
                setError(null);
                setStep('credentials');
              }}
            >
              Back
            </Button>
          </form>
        ) : (
          <form className="flex flex-col gap-1" onSubmit={handleCredentials}>
            {!knownTenantSlug && (
              <FormField label="Organization slug" htmlFor="login-tenant" hint="e.g. acme-live">
                <Input
                  id="login-tenant"
                  autoComplete="organization"
                  autoFocus
                  value={tenantSlug}
                  onChange={(e) => setTenantSlug(e.target.value)}
                />
              </FormField>
            )}
            <FormField label="Email" htmlFor="login-email">
              <Input
                id="login-email"
                type="email"
                autoComplete="username"
                autoFocus={!!knownTenantSlug}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormField>
            <FormField label="Password" htmlFor="login-password">
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </FormField>
            {error && <ErrorSummary error={error} className="mb-1" />}
            <Button type="submit" disabled={submitting} className="w-full justify-center mt-2">
              {submitting ? 'Signing in…' : 'Sign in'}
            </Button>
            <Link
              to="/forgot-password"
              className="mt-2 text-center text-sm text-slate-500 hover:text-slate-700"
            >
              Forgot password?
            </Link>
          </form>
        )}
      </Card>
    </div>
  );
}
